import { handleAjaxError } from "../actions/handleAjaxError";
import { getChatAjaxConfig } from "./chatSettingsAjaxConfig";
import { buildMemoryActionsMenu, buildMemoryDuplicateNote, buildMemoryStatusNote } from "./chatSettingsMemories.cjs";
import { hideChatGlobalLoader, showChatGlobalLoader } from "./chatGlobalLoader";

const SETTINGS_TARGET_MEMORY_PARAM = 'gt_memory_id';
const TARGET_MEMORY_HIGHLIGHT_CLASS = 'anim-shake';
const TARGET_MEMORY_HIGHLIGHT_DURATION = 3000;
const IMPORT_MEMORY_MODAL_ID = 'growtypeChatImportMemoryModal';
const IMPORT_MEMORY_MODAL_HASH = '#import-memories';

function isImportMemoryModalHash() {
    return (window.location.hash || '').toLowerCase() === IMPORT_MEMORY_MODAL_HASH;
}

function updateImportMemoryModalHash() {
    if (isImportMemoryModalHash()) {
        return;
    }

    const newUrl = `${window.location.pathname}${window.location.search}${IMPORT_MEMORY_MODAL_HASH}`;
    window.history.replaceState(window.history.state, document.title, newUrl);
}

function clearImportMemoryModalHash() {
    if (!isImportMemoryModalHash()) {
        return;
    }

    const newUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(window.history.state, document.title, newUrl);
}

function openImportMemoryModalFromHash() {
    if (!isImportMemoryModalHash()) {
        return;
    }

    const modal = document.getElementById(IMPORT_MEMORY_MODAL_ID);
    if (!modal) {
        return;
    }

    const $modal = $(modal);
    if (!$modal.parent().is('body')) {
        $modal.appendTo('body');
    }

    if (modal.classList.contains('show')) {
        return;
    }

    if (window.growtypeModal?.show) {
        window.growtypeModal.show(IMPORT_MEMORY_MODAL_ID, { stack: true });
    } else if (typeof $.fn.modal === 'function') {
        $modal.modal('show');
    }
}

function getTargetMemoryIdFromUrl() {
    try {
        const url = new URL(window.location.href);
        return (url.searchParams.get(SETTINGS_TARGET_MEMORY_PARAM) || '').trim();
    } catch (error) {
        return '';
    }
}

function clearTargetMemoryIdFromUrl() {
    try {
        const url = new URL(window.location.href);

        if (!url.searchParams.has(SETTINGS_TARGET_MEMORY_PARAM)) {
            return;
        }

        url.searchParams.delete(SETTINGS_TARGET_MEMORY_PARAM);
        window.history.replaceState({}, document.title, url.toString());
    } catch (error) {
        // no-op
    }
}

function getPendingTargetMemoryId($modal = $('#growtypeChatSettingsModal')) {
    const modalMemoryId = String($modal.data('targetMemoryId') || '').trim();
    return modalMemoryId || getTargetMemoryIdFromUrl();
}

function focusTargetMemory(container) {
    if (!container || container.length === 0) {
        return false;
    }

    const $modal = $('#growtypeChatSettingsModal');
    const targetMemoryId = getPendingTargetMemoryId($modal);

    if (!targetMemoryId) {
        return false;
    }

    const targetItem = container
        .find('.chat-settings-memory-item')
        .filter(function () {
            return String($(this).attr('data-memory-id') || '').trim() === targetMemoryId;
        })
        .first();

    if (!targetItem.length) {
        return false;
    }

    targetItem[0].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
    });

    targetItem.addClass(TARGET_MEMORY_HIGHLIGHT_CLASS);

    window.setTimeout(() => {
        targetItem.removeClass(TARGET_MEMORY_HIGHLIGHT_CLASS);
    }, TARGET_MEMORY_HIGHLIGHT_DURATION);

    $modal.removeData('targetMemoryId');
    clearTargetMemoryIdFromUrl();

    return true;
}

export function bindMemoriesManager() {
    const modalSelector = '#growtypeChatSettingsModal';
    const memoriesSelector = '.chat-settings-memories';
    const addMemoryModalSelector = '#growtypeChatAddMemoryModal';
    const importMemoryModalSelector = `#${IMPORT_MEMORY_MODAL_ID}`;

    const getAddMemoryModal = () => $(addMemoryModalSelector);
    const getManualMemoryMaxLength = () => {
        const configuredLimit = Number.parseInt(
            getAddMemoryModal().find('textarea[name="manual_memory_value"]').attr('maxlength') || '',
            10
        );

        return Number.isFinite(configuredLimit) ? configuredLimit : 1000;
    };
    const updateMemoryCharacterCounter = (textarea) => {
        const field = $(textarea);
        const maxLength = Number.parseInt(field.attr('maxlength') || '', 10);
        const counter = field.closest('.chat-settings-memory-edit-field').find('.chat-settings-memory-character-count').first();

        if (!counter.length || !Number.isFinite(maxLength)) {
            return;
        }

        const currentLength = Array.from(String(field.val() || '')).length;
        counter.text(`${currentLength.toLocaleString()} / ${maxLength.toLocaleString()}`);
        counter.toggleClass('is-at-limit', currentLength >= maxLength);
    };
    const getFallbackMemoriesContainer = () => {
        const visibleContainers = $(memoriesSelector).filter(function () {
            return $(this).is(':visible');
        });

        if (visibleContainers.length === 1) {
            return visibleContainers.first();
        }

        const accountPageContainer = $('.woocommerce-EditAccountForm').find(memoriesSelector).first();
        if (accountPageContainer.length) {
            return accountPageContainer;
        }

        return visibleContainers.first();
    };
    const getMemoriesContainerForModal = () => {
        const modal = getAddMemoryModal();
        const container = modal.data('targetMemoriesContainer');

        if (container && container.length) {
            return container;
        }

        const fallbackContainer = getFallbackMemoriesContainer();

        if (fallbackContainer.length) {
            modal.data('targetMemoriesContainer', fallbackContainer);
            return fallbackContainer;
        }

        return $();
    };

    const updateAddMemoryModalCopy = (container = $()) => {
        const modal = getAddMemoryModal();
        const scopeLabel = String(container.attr('data-memory-scope-label') || '').trim();
        const scopeText = modal.find('.chat-settings-memories-form-scope');
        const defaultLabel = String(scopeText.attr('data-default-label') || 'Saving to memories').trim();

        if (scopeText.length) {
            scopeText.text(scopeLabel ? `${defaultLabel}: ${scopeLabel}` : defaultLabel);
        }
    };

    const setAddMemoryModalFeedback = (message = '', type = '') => {
        const feedback = getAddMemoryModal().find('.chat-settings-memories-form-feedback');
        feedback.removeClass('is-error is-success');

        if (!message) {
            feedback.text('');
            return;
        }

        feedback.text(message);
        feedback.addClass(type === 'success' ? 'is-success' : 'is-error');
    };

    const resetAddMemoryModalState = () => {
        const modal = getAddMemoryModal();

        modal.removeData('targetMemoriesContainer');
        const memoryTextarea = modal.find('textarea[name="manual_memory_value"]');
        memoryTextarea.val('');
        updateMemoryCharacterCounter(memoryTextarea);
        modal.find('select[name="manual_memory_category"], .chat-settings-memory-category-edit').val('freeform').trigger('chosen:updated');
        modal.find('input[name="manual_memory_importance"], .chat-settings-memory-importance-edit').val('85');
        modal.find('.chat-settings-memories-add').prop('disabled', false);
        setAddMemoryModalFeedback('', '');
        updateAddMemoryModalCopy();
    };

    const loadVisibleMemories = (forceReload = false) => {
        $(memoriesSelector).each(function () {
            const container = $(this);

            if (!container.is(':visible')) {
                return;
            }

            loadMemories(container, forceReload);
        });
    };

    $(document)
        .off('shown.bs.tab.memories', `${modalSelector} button[data-bs-target="#memories-content"]`)
        .on('shown.bs.tab.memories', `${modalSelector} button[data-bs-target="#memories-content"]`, function () {
            const container = $(`${modalSelector} ${memoriesSelector}`);
            loadMemories(container);
        });

    $(document)
        .off('shown.bs.modal.memories', modalSelector)
        .on('shown.bs.modal.memories', modalSelector, function () {
            const activeMemoriesTab = $(this).find('button[data-bs-target="#memories-content"].active');
            if (activeMemoriesTab.length) {
                loadMemories($(this).find(memoriesSelector));
            }
        });

    $(document)
        .off('shown.bs.modal.memoriesAddState', addMemoryModalSelector)
        .on('shown.bs.modal.memoriesAddState', addMemoryModalSelector, function () {
            const container = getMemoriesContainerForModal();

            updateAddMemoryModalCopy(container);
            setAddMemoryModalFeedback('', '');

            const memoryTextarea = $(this).find('textarea[name="manual_memory_value"]');
            updateMemoryCharacterCounter(memoryTextarea);
            memoryTextarea.trigger('focus');
        })
        .off('hidden.bs.modal.memoriesAddState', addMemoryModalSelector)
        .on('hidden.bs.modal.memoriesAddState', addMemoryModalSelector, function () {
            resetAddMemoryModalState();
        });

    $(document)
        .off('show.bs.modal.importMemoryHash', importMemoryModalSelector)
        .on('show.bs.modal.importMemoryHash', importMemoryModalSelector, updateImportMemoryModalHash)
        .off('hidden.bs.modal.importMemoryHash', importMemoryModalSelector)
        .on('hidden.bs.modal.importMemoryHash', importMemoryModalSelector, clearImportMemoryModalHash);

    $(window)
        .off('hashchange.growtypeChatImportMemoryHash')
        .on('hashchange.growtypeChatImportMemoryHash', openImportMemoryModalFromHash);

    openImportMemoryModalFromHash();

    $(document)
        .off('click.memoriesRefresh', '.chat-settings-memories-refresh')
        .on('click.memoriesRefresh', '.chat-settings-memories-refresh', function () {
            loadMemories($(this).closest(memoriesSelector), true);
        });

    $(document)
        .off('click.toggleAddMemory', '.chat-settings-memories-toggle-add')
        .on('click.toggleAddMemory', '.chat-settings-memories-toggle-add', function (e) {
            e.preventDefault();
            e.stopPropagation();

            const container = $(this).closest(memoriesSelector);
            const $addModal = getAddMemoryModal();
            if ($addModal.length) {
                if (!$addModal.parent().is('body')) {
                    $addModal.appendTo('body');
                }

                $addModal.data('targetMemoriesContainer', container);
                updateAddMemoryModalCopy(container);

                if (window.growtypeModal?.show) {
                    window.growtypeModal.show('growtypeChatAddMemoryModal', { stack: true });
                } else if (typeof $.fn.modal === 'function') {
                    $addModal.modal('show');
                }
            }
        });

    initMultiModalStacking();

    $(document)
        .off('input.memoryCharacterCounter', '.chat-settings-memory-edit-input')
        .on('input.memoryCharacterCounter', '.chat-settings-memory-edit-input', function () {
            updateMemoryCharacterCounter(this);
        });

    $(document)
        .off('click.importMemoryCopy', '.chat-settings-import-memory-copy')
        .on('click.importMemoryCopy', '.chat-settings-import-memory-copy', function (e) {
            e.preventDefault();
            const btn = $(this);
            const textToCopy = (btn.closest('.import-memory-prompt-box').find('.import-memory-prompt-text').text() || '').trim();

            if (!textToCopy) return;

            const copySuccess = () => {
                const originalHtml = btn.html();
                btn.html('<i class="fa-solid fa-check me-1"></i><span>Copied!</span>');
                setTimeout(() => btn.html(originalHtml), 2000);
            };

            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(textToCopy).then(copySuccess).catch(() => {});
            } else {
                const dummy = document.createElement('textarea');
                dummy.value = textToCopy;
                document.body.appendChild(dummy);
                dummy.select();
                document.execCommand('copy');
                document.body.removeChild(dummy);
                copySuccess();
            }
        });

    $(document)
        .off('click.importMemorySubmit', '.chat-settings-import-memory-submit')
        .on('click.importMemorySubmit', '.chat-settings-import-memory-submit', function (e) {
            e.preventDefault();
            const submitButton = $(this);
            const modal = submitButton.closest('#growtypeChatImportMemoryModal');
            const textarea = modal.find('textarea[name="import_memory_value"]');
            const feedback = modal.find('.chat-settings-memories-import-feedback');
            const memoryValue = (textarea.val() || '').trim();
            const container = getFallbackMemoriesContainer();

            if (!memoryValue) {
                feedback.text('Please paste your memory details first.');
                return;
            }

            const ajaxConfig = getChatAjaxConfig();

            if (!ajaxConfig.url || !ajaxConfig.memories_add_action || !ajaxConfig.nonce) {
                feedback.text('Could not add memory.');
                return;
            }

            feedback.text('');
            submitButton.prop('disabled', true);
            showChatGlobalLoader(modal, {
                text: 'Importing memories',
            });

            $.ajax({
                url: ajaxConfig.url,
                type: 'post',
                data: {
                    action: ajaxConfig.memories_add_action,
                    session_token: container.attr('data-session-token'),
                    memory_value: memoryValue,
                    input_mode: 'import',
                    category: 'freeform',
                    importance: 85,
                    nonce: ajaxConfig.nonce
                },
                success: function (response) {
                    if (response?.success) {
                        textarea.val('');
                        hideChatGlobalLoader(modal);
                        if (window.growtypeModal?.hide) {
                            window.growtypeModal.hide('growtypeChatImportMemoryModal');
                        } else if (typeof $.fn.modal === 'function') {
                            modal.modal('hide');
                        }
                        if (container.length) {
                            renderMemories(container, response.data?.memories || []);
                            container.attr('data-memories-loaded', '1');
                            setMemoriesFeedback(container, response.data?.message || 'Memory imported successfully.', 'success');
                        }
                    } else {
                        feedback.text(response?.data?.message || 'Could not import memory.');
                    }
                },
                error: function (response) {
                    feedback.text(response?.responseJSON?.data?.message || 'Could not import memory.');
                    handleAjaxError(response);
                },
                complete: function () {
                    hideChatGlobalLoader(modal);
                    submitButton.prop('disabled', false);
                }
            });
        });

    $(document)
        .off('click.memoriesAdd', '#growtypeChatAddMemoryModal .chat-settings-memories-add, .chat-settings-memories-add')
        .on('click.memoriesAdd', '#growtypeChatAddMemoryModal .chat-settings-memories-add, .chat-settings-memories-add', function (e) {
            e.preventDefault();
            const submitButton = $(this);
            const container = $(this).closest(memoriesSelector).length
                ? $(this).closest(memoriesSelector)
                : getMemoriesContainerForModal();
            const form = $(this).closest(addMemoryModalSelector).length
                ? getAddMemoryModal()
                : $(this).closest(memoriesSelector);
            const textarea = form.find('textarea[name="manual_memory_value"]').first();
            const categorySelect = form.find('select[name="manual_memory_category"], .chat-settings-memory-category-edit').first();
            const importanceInput = form.find('input[name="manual_memory_importance"], .chat-settings-memory-importance-edit').first();

            const memoryValue = (textarea.val() || '').trim();
            const category = (categorySelect.val() || 'freeform').trim();
            const importance = Number.parseInt(importanceInput.val() || '85', 10);

            if (!container.length) {
                setAddMemoryModalFeedback('Could not find where to save this memory.', 'error');
                return;
            }

            if (!memoryValue) {
                setMemoriesFeedback(container, 'Please enter a memory first.', 'error');
                setAddMemoryModalFeedback('Please enter a memory first.', 'error');
                return;
            }

            setMemoriesBusy(container, true);
            setMemoriesFeedback(container, '', '');
            setAddMemoryModalFeedback('', '');
            submitButton.prop('disabled', true);

            const ajaxConfig = getChatAjaxConfig();

            if (!ajaxConfig.url || !ajaxConfig.memories_add_action || !ajaxConfig.nonce) {
                setMemoriesBusy(container, false);
                setMemoriesFeedback(container, 'Could not add memory.', 'error');
                setAddMemoryModalFeedback('Could not add memory.', 'error');
                submitButton.prop('disabled', false);
                return;
            }

            $.ajax({
                url: ajaxConfig.url,
                type: 'post',
                data: {
                    action: ajaxConfig.memories_add_action,
                    session_token: container.attr('data-session-token'),
                    memory_value: memoryValue,
                    input_mode: 'manual',
                    category: category,
                    importance: Number.isFinite(importance) ? importance : 85,
                    nonce: ajaxConfig.nonce
                },
                success: function (response) {
                    if (response?.success) {
                        textarea.val('');
                        setAddMemoryModalFeedback(response.data?.message || 'Memory added.', 'success');
                        if (window.growtypeModal?.hide) {
                            window.growtypeModal.hide('growtypeChatAddMemoryModal');
                        } else {
                            getAddMemoryModal().modal('hide');
                        }
                        renderMemories(container, response.data?.memories || []);
                        container.attr('data-memories-loaded', '1');
                        setMemoriesFeedback(container, response.data?.message || 'Memory added.', 'success');
                    } else {
                        setMemoriesFeedback(container, response?.data?.message || 'Could not add memory.', 'error');
                        setAddMemoryModalFeedback(response?.data?.message || 'Could not add memory.', 'error');
                    }
                },
                error: function (response) {
                    setMemoriesFeedback(container, response?.responseJSON?.data?.message || 'Could not add memory.', 'error');
                    setAddMemoryModalFeedback(response?.responseJSON?.data?.message || 'Could not add memory.', 'error');
                    handleAjaxError(response);
                },
                complete: function () {
                    setMemoriesBusy(container, false);
                    submitButton.prop('disabled', false);
                }
            });
        });

    loadVisibleMemories();

    const closeMemoryActionsMenus = (exceptMenu = null) => {
        $('.chat-settings-memory-menu').each(function () {
            const menu = $(this);

            if (exceptMenu?.length && menu.is(exceptMenu)) {
                return;
            }

            menu.removeClass('is-open');
            menu.find('.chat-settings-memory-menu-trigger').attr('aria-expanded', 'false');
            menu.find('.chat-settings-memory-menu-list').addClass('d-none');
        });
    };

    if (document.growtypeChatMemoryMenuEscapeHandler) {
        document.removeEventListener('keydown', document.growtypeChatMemoryMenuEscapeHandler, true);
    }

    document.growtypeChatMemoryMenuEscapeHandler = function (event) {
        if (event.key !== 'Escape') {
            return;
        }

        const openMenu = $('.chat-settings-memory-menu.is-open').first();

        if (!openMenu.length) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        closeMemoryActionsMenus();
        openMenu.find('.chat-settings-memory-menu-trigger').trigger('focus');
    };

    document.addEventListener('keydown', document.growtypeChatMemoryMenuEscapeHandler, true);

    $(document)
        .off('click.memoryMenu', '.chat-settings-memory-menu-trigger')
        .off('click.memoryMenuItem', '.chat-settings-memory-menu-item')
        .off('click.memoryMenuOutside')
        .off('click.memoryDelete', '.chat-settings-memory-delete')
        .off('click.memoryMoveGlobal', '.chat-settings-memory-move-global')
        .on('click.memoryMenu', '.chat-settings-memory-menu-trigger', function (event) {
            event.preventDefault();
            event.stopPropagation();

            const menu = $(this).closest('.chat-settings-memory-menu');
            const shouldOpen = !menu.hasClass('is-open');

            closeMemoryActionsMenus(menu);
            menu.toggleClass('is-open', shouldOpen);
            $(this).attr('aria-expanded', shouldOpen ? 'true' : 'false');
            menu.find('.chat-settings-memory-menu-list').toggleClass('d-none', !shouldOpen);
        })
        .on('click.memoryMenuItem', '.chat-settings-memory-menu-item', function () {
            closeMemoryActionsMenus();
        })
        .on('click.memoryMenuOutside', function (event) {
            if (!$(event.target).closest('.chat-settings-memory-menu').length) {
                closeMemoryActionsMenus();
            }
        })
        .on('click.memoryClear', '.chat-settings-memories-clear', function () {
            const container = $(this).closest(memoriesSelector);
            const scopeLabel = String(container.attr('data-memory-scope-label') || 'memories').trim().toLowerCase();
            const confirmationMessage = `Are you sure you want to clear all ${scopeLabel}? This action cannot be undone.`;

            if (!window.confirm(confirmationMessage)) {
                return;
            }

            const ajaxConfig = getChatAjaxConfig();
            if (!ajaxConfig.url || !ajaxConfig.memories_clear_action || !ajaxConfig.nonce) {
                setMemoriesFeedback(container, 'Could not clear memories.', 'error');
                return;
            }

            setMemoriesBusy(container, true);
            setMemoriesFeedback(container, '', '');

            $.ajax({
                url: ajaxConfig.url,
                type: 'post',
                data: {
                    action: ajaxConfig.memories_clear_action,
                    session_token: container.attr('data-session-token'),
                    nonce: ajaxConfig.nonce
                },
                success: function (response) {
                    if (response?.success) {
                        renderMemories(container, response.data?.memories || []);
                        setMemoriesFeedback(container, response.data?.message || 'Memories cleared.', 'success');
                    } else {
                        setMemoriesFeedback(container, response?.data?.message || 'Could not clear memories.', 'error');
                    }
                },
                error: function (response) {
                    setMemoriesFeedback(container, response?.responseJSON?.data?.message || 'Could not clear memories.', 'error');
                    handleAjaxError(response);
                },
                complete: function () {
                    setMemoriesBusy(container, false);
                }
            });
        })
        .on('click.memoryMoveGlobal', '.chat-settings-memory-move-global', function () {
            closeMemoryActionsMenus();
            const container = $(this).closest(memoriesSelector);
            const memoryId = $(this).attr('data-memory-id');
            const ajaxConfig = getChatAjaxConfig();

            if (!memoryId) {
                return;
            }

            if (!ajaxConfig.url || !ajaxConfig.memories_move_to_global_action || !ajaxConfig.nonce) {
                setMemoriesFeedback(container, 'Could not move memory to global memories.', 'error');
                return;
            }

            setMemoriesBusy(container, true);
            setMemoriesFeedback(container, '', '');

            $.ajax({
                url: ajaxConfig.url,
                type: 'post',
                data: {
                    action: ajaxConfig.memories_move_to_global_action,
                    session_token: container.attr('data-session-token'),
                    memory_id: memoryId,
                    nonce: ajaxConfig.nonce
                },
                success: function (response) {
                    if (response?.success) {
                        renderMemories(container, response.data?.memories || []);
                        setMemoryMovedToGlobalFeedback(container);
                    } else {
                        setMemoriesFeedback(container, response?.data?.message || 'Could not move memory to global memories.', 'error');
                    }
                },
                error: function (response) {
                    setMemoriesFeedback(container, response?.responseJSON?.data?.message || 'Could not move memory to global memories.', 'error');
                    handleAjaxError(response);
                },
                complete: function () {
                    setMemoriesBusy(container, false);
                }
            });
        })
        .on('click.memoryDelete', '.chat-settings-memory-delete', function () {
            closeMemoryActionsMenus();
            const container = $(this).closest(memoriesSelector);
            const memoryId = $(this).attr('data-memory-id');

            if (!memoryId) {
                return;
            }

            setMemoriesBusy(container, true);
            setMemoriesFeedback(container, '', '');

            const ajaxConfig = getChatAjaxConfig();

            if (!ajaxConfig.url || !ajaxConfig.memories_delete_action || !ajaxConfig.nonce) {
                setMemoriesBusy(container, false);
                setMemoriesFeedback(container, 'Could not delete memory.', 'error');
                return;
            }

            $.ajax({
                url: ajaxConfig.url,
                type: 'post',
                data: {
                    action: ajaxConfig.memories_delete_action,
                    session_token: container.attr('data-session-token'),
                    memory_id: memoryId,
                    nonce: ajaxConfig.nonce
                },
                success: function (response) {
                    if (response?.success) {
                        renderMemories(container, response.data?.memories || []);
                        setMemoriesFeedback(container, response.data?.message || 'Memory deleted.', 'success');
                    } else {
                        setMemoriesFeedback(container, response?.data?.message || 'Could not delete memory.', 'error');
                    }
                },
                error: function (response) {
                    setMemoriesFeedback(container, response?.responseJSON?.data?.message || 'Could not delete memory.', 'error');
                    handleAjaxError(response);
                },
                complete: function () {
                    setMemoriesBusy(container, false);
                }
            });
        })
        .on('click.memoryEdit', '.chat-settings-memory-edit', function () {
            const btn = $(this);
            const item = btn.closest('.chat-settings-memory-item');
            const valueDisplay = item.find('.chat-settings-memory-value');
            const categorySpan = item.find('.chat-settings-memory-category');
            const importanceSpan = item.find('.chat-settings-memory-importance');

            const currentValue = valueDisplay.text().trim();
            const currentCategory = (categorySpan.text() || 'freeform').trim();
            const currentImportance = Number.parseInt(importanceSpan.text().replace(/[^\d]/g, '') || '8', 10);

            closeMemoryActionsMenus();
            item.addClass('is-editing');
            valueDisplay.addClass('d-none');

            if (item.find('.chat-settings-memory-edit-fields').length === 0) {
                const categories = ['freeform', 'preference', 'personality', 'relationship', 'fact', 'background'];
                if (!categories.includes(currentCategory)) {
                    categories.push(currentCategory);
                }
                const optionsHtml = categories.map(c => `<option value="${c}" ${c === currentCategory ? 'selected' : ''}>${c}</option>`).join('');

                const editFieldsHtml = `
                    <div class="chat-settings-memory-edit-fields">
                        <div class="chat-settings-memory-edit-row">
                            <div class="chat-settings-memory-edit-field">
                                <label class="chat-settings-memory-edit-label">Category</label>
                                <select class="select select-sm chat-settings-memory-category-edit">${optionsHtml}</select>
                            </div>
                            <div class="chat-settings-memory-edit-field">
                                <label class="chat-settings-memory-edit-label">Score (0-100)</label>
                                <input type="number" min="0" max="100" class="input input-sm chat-settings-memory-importance-edit" value="${currentImportance}">
                            </div>
                        </div>
                        <div class="chat-settings-memory-edit-field mt-3">
                            <label class="chat-settings-memory-edit-label">Memory Content</label>
                            <textarea class="textarea chat-settings-memory-edit-input" rows="3" maxlength="${getManualMemoryMaxLength()}">${currentValue}</textarea>
                            <div class="chat-settings-memory-character-limit">
                                <span>Maximum ${getManualMemoryMaxLength().toLocaleString()} characters.</span>
                                <span class="chat-settings-memory-character-count" aria-live="polite"></span>
                            </div>
                        </div>
                    </div>
                `;

                $(editFieldsHtml).prependTo(item.find('.chat-settings-memory-item-main'));
            } else {
                item.find('.chat-settings-memory-category-edit').val(currentCategory);
                item.find('.chat-settings-memory-importance-edit').val(currentImportance);
                item.find('.chat-settings-memory-edit-input').val(currentValue);
                item.find('.chat-settings-memory-edit-fields').removeClass('d-none');
            }

            item.find('.chat-settings-memory-menu').addClass('d-none');
            item.find('.chat-settings-memory-save, .chat-settings-memory-cancel').removeClass('d-none');
            updateMemoryCharacterCounter(item.find('.chat-settings-memory-edit-input'));
        })
        .on('click.memoryCancel', '.chat-settings-memory-cancel', function () {
            const btn = $(this);
            const item = btn.closest('.chat-settings-memory-item');

            item.removeClass('is-editing');
            item.find('.chat-settings-memory-value').removeClass('d-none');
            item.find('.chat-settings-memory-edit-fields').addClass('d-none');
            item.find('.chat-settings-memory-menu').removeClass('d-none');
            item.find('.chat-settings-memory-save, .chat-settings-memory-cancel').addClass('d-none');
        })
        .on('click.memorySave', '.chat-settings-memory-save', function () {
            const btn = $(this);
            const item = btn.closest('.chat-settings-memory-item');
            const container = item.closest(memoriesSelector);
            const newValue = item.find('.chat-settings-memory-edit-input').val().trim();
            const newCategory = item.find('.chat-settings-memory-category-edit').val() || 'freeform';
            const newImportance = Number.parseInt(item.find('.chat-settings-memory-importance-edit').val() || '8', 10);

            if (!newValue) {
                setMemoriesFeedback(container, 'Memory text cannot be empty.', 'error');
                return;
            }

            updateMemory(container, item.attr('data-memory-id'), newValue, newCategory, newImportance);
        });
}

function loadMemories(container, forceReload = false) {
    if (!container || container.length === 0) {
        return;
    }

    if (container.attr('data-memories-loaded') === '1' && !forceReload) {
        focusTargetMemory(container);
        return;
    }

    setMemoriesBusy(container, true, true);
    setMemoriesFeedback(container, '', '');

    const ajaxConfig = getChatAjaxConfig();

    if (!ajaxConfig.url || !ajaxConfig.memories_list_action || !ajaxConfig.nonce) {
        setMemoriesBusy(container, false);
        setMemoriesFeedback(container, 'Could not load memories.', 'error');
        return;
    }

    $.ajax({
        url: ajaxConfig.url,
        type: 'post',
        data: {
            action: ajaxConfig.memories_list_action,
            session_token: container.attr('data-session-token'),
            nonce: ajaxConfig.nonce
        },
        success: function (response) {
            if (response?.success) {
                renderMemories(container, response.data?.memories || []);
                container.attr('data-memories-loaded', '1');
                focusTargetMemory(container);
            } else {
                setMemoriesFeedback(container, response?.data?.message || 'Could not load memories.', 'error');
            }
        },
        error: function (response) {
            setMemoriesFeedback(container, response?.responseJSON?.data?.message || 'Could not load memories.', 'error');
            handleAjaxError(response);
        },
        complete: function () {
            setMemoriesBusy(container, false);
        }
    });
}

function updateMemory(container, memoryId, newValue, category, importance) {
    setMemoriesBusy(container, true);
    setMemoriesFeedback(container, '');

    const ajaxConfig = getChatAjaxConfig();

    if (!ajaxConfig.url || !ajaxConfig.memories_update_action || !ajaxConfig.nonce) {
        setMemoriesBusy(container, false);
        setMemoriesFeedback(container, 'Failed to update memory. Please try again.', 'error');
        return;
    }

    $.ajax({
        url: ajaxConfig.url,
        type: 'post',
        data: {
            action: ajaxConfig.memories_update_action,
            session_token: container.attr('data-session-token'),
            memory_id: memoryId,
            memory_value: newValue,
            category: category,
            importance: importance,
            nonce: ajaxConfig.nonce
        },
        success: function (response) {
            if (response.success) {
                renderMemories(container, response.data.memories);
                setMemoriesFeedback(container, 'Memory updated successfully.', 'success');
            } else {
                setMemoriesFeedback(container, response.data.message || 'Failed to update memory.', 'error');
            }
        },
        error: function () {
            setMemoriesFeedback(container, 'Failed to update memory. Please try again.', 'error');
        },
        complete: function () {
            setMemoriesBusy(container, false);
        }
    });
}

function renderMemories(container, memories) {
    const list = container.find('.chat-settings-memories-list');
    const empty = container.find('.chat-settings-memories-empty');
    const count = Array.isArray(memories) ? memories.length : 0;
    const memoryScope = container.attr('data-memory-scope') || '';

    setMemoriesCount(container, count);

    if (count === 0) {
        list.html('');
        empty.removeClass('d-none');
        return;
    }

    empty.addClass('d-none');

    const html = memories.map((memory, index) => {
        const number = index + 1;
        const category = escapeHtml(memory.category || 'freeform');
        const value = escapeHtml(memory.memory_value || '');
        const importance = Number.parseInt(memory.importance || 0, 10);
        const updatedAt = escapeHtml(memory.updated_at || '');
        const duplicateNote = escapeHtml(buildMemoryDuplicateNote(memory));
        const statusNote = escapeHtml(buildMemoryStatusNote(memory));
        const confidenceLabel = escapeHtml(memory.confidence_label || '');
        const whySaved = escapeHtml(memory.why_saved || '');
        const freshnessLabel = escapeHtml(memory.freshness_label || '');
        const memoryTypeLabel = escapeHtml(memory.memory_type_label || '');
        const stabilityLabel = escapeHtml(memory.stability_label || '');
        const statusLabel = escapeHtml(memory.status_label || '');
        const evidenceCount = Number.parseInt(memory.evidence_count || 0, 10);

        return `
            <div class="chat-settings-memory-item" data-memory-id="${memory.id}">
                <div class="chat-settings-memory-item-header">
                    <div class="chat-settings-memory-meta">
                        <span class="chat-settings-memory-number">${number}.</span>
                        <span class="chat-settings-memory-category">${category}</span>
                        <span class="chat-settings-memory-importance">${importance}</span>
                        ${memoryTypeLabel ? `<span class="chat-settings-memory-confidence">${memoryTypeLabel}</span>` : ''}
                        ${stabilityLabel ? `<span class="chat-settings-memory-freshness">${stabilityLabel}</span>` : ''}
                        ${statusLabel ? `<span class="chat-settings-memory-evidence">${statusLabel}</span>` : ''}
                        ${confidenceLabel ? `<span class="chat-settings-memory-confidence">${confidenceLabel}</span>` : ''}
                        ${evidenceCount > 0 ? `<span class="chat-settings-memory-evidence">${evidenceCount} evidence</span>` : ''}
                        ${freshnessLabel ? `<span class="chat-settings-memory-freshness">${freshnessLabel}</span>` : ''}
                    </div>
                    <div class="chat-settings-memory-actions">
                        ${buildMemoryActionsMenu(memory.id, memoryScope)}
                        <button type="button" class="btn btn-primary btn-sm chat-settings-memory-save d-none" data-memory-id="${memory.id}">Save</button>
                        <button type="button" class="btn btn-secondary btn-sm chat-settings-memory-cancel d-none">Cancel</button>
                    </div>
                </div>
                <div class="chat-settings-memory-item-main">
                    <div class="chat-settings-memory-value">${value}</div>
                    ${whySaved ? `<div class="chat-settings-memory-reason">${whySaved}</div>` : ''}
                    <div class="chat-settings-memory-date">${updatedAt}${duplicateNote}${statusNote}</div>
                </div>
            </div>
        `;
    }).join('');

    list.html(html);
}

function setMemoriesCount(container, count) {
    const normalizedCount = Number.isFinite(count) ? count : 0;
    container.find('.chat-settings-memories-count').text(normalizedCount);
    container.find('.chat-settings-memories-clear').prop('disabled', normalizedCount === 0);
}

function setMemoriesBusy(container, isBusy, loadingOnly = false) {
    container.toggleClass('is-loading', isBusy);
    container.find('.chat-settings-memories-loading').toggleClass('d-none', !isBusy);

    if (!loadingOnly) {
        container.find('.chat-settings-memories-add, .chat-settings-memories-refresh, .chat-settings-memories-clear, .chat-settings-memory-menu-trigger, .chat-settings-memory-menu-item').prop('disabled', isBusy);

        if (!isBusy) {
            const memoryCount = Number.parseInt(container.find('.chat-settings-memories-count').text() || '0', 10);
            container.find('.chat-settings-memories-clear').prop('disabled', !Number.isFinite(memoryCount) || memoryCount === 0);
        }
    }
}

function setMemoriesFeedback(container, message, type) {
    const feedback = container.find('.chat-settings-memories-feedback');
    feedback.removeClass('is-error is-success');

    if (!message) {
        feedback.text('');
        return;
    }

    feedback.text(message);
    feedback.addClass(type === 'success' ? 'is-success' : 'is-error');
}

function setMemoryMovedToGlobalFeedback(container) {
    const feedback = container.find('.chat-settings-memories-feedback');
    const globalMemoriesUrl = String(container.attr('data-global-memories-url') || '').trim();

    feedback.removeClass('is-error').addClass('is-success').empty();
    feedback.append(document.createTextNode('Memory moved to '));

    if (globalMemoriesUrl) {
        $('<a>', {
            href: globalMemoriesUrl,
            text: 'global memories',
        }).appendTo(feedback);
    } else {
        feedback.append(document.createTextNode('global memories'));
    }

    feedback.append(document.createTextNode('.'));
}

function escapeHtml(value) {
    return $('<div>').text(value || '').html();
}

function initMultiModalStacking() {
    $(document)
        .off('hidden.bs.modal.stacking', '.modal')
        .on('hidden.bs.modal.stacking', '.modal', function () {
            if (window.growtypeModal?.syncState) {
                window.growtypeModal.syncState();
            }
        });
}
